import {
  type PersistedResourceIdentifier,
  type RelationshipData,
  type ResourceId,
  type ResourceType,
} from '../types'

/** Used as a property in maps for quick access of resources */
export type ResourceKey<TType extends ResourceType = ResourceType> =
  `${ResourceId}:${TType}`

type ResourceLike<TType extends ResourceType = ResourceType> = Pick<
  PersistedResourceIdentifier<TType>,
  'id' | 'type'
>

type DataRelationship<TData extends RelationshipData> = {
  readonly data: TData
}

export type NullResourceLinkage = DataRelationship<null>

export type SingleResourceLinkage<TType extends string> = DataRelationship<
  ResourceLike<TType>
>

export type CollectionResourceLinkage<TType extends string> = DataRelationship<
  Array<ResourceLike<TType>>
>

export class ResourceLinkage<
  const TType extends ResourceType,
  const TResource extends ResourceLike<TType>,
> {
  private readonly id: ResourceId
  private readonly type: TResource['type']

  constructor(resourceLike: TResource) {
    this.id = resourceLike.id
    this.type = resourceLike.type
  }

  get key(): ResourceKey<TResource['type']> {
    return `${this.id}:${this.type}`
  }

  get identifier(): ResourceLike<TResource['type']> {
    return {
      id: this.id,
      type: this.type,
    } as const
  }
}
